import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, Checkbox, Popconfirm, message, Space, Tag } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useIR35List, useIR35Create, useIR35Update, useIR35Delete, useCCUsers } from "../../../../api-hooks/compliance";

const DET_COLOR: Record<string, string> = { inside: "red", outside: "green", undetermined: "orange" };

export default function IR35Tab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [modal, setModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [form] = Form.useForm();
  const [page, setPage] = useState(1);

  const { data: res, isLoading } = useIR35List(cid, { page, limit: 20 });
  const { data: users = [] } = useCCUsers(cid);
  const rows = (res as any)?.data ?? [];
  const total = (res as any)?.total ?? 0;

  const cb = (msg: string) => ({ onSuccess: () => { msgApi.success(msg); setModal({ open: false, rec: null }); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = useIR35Create(cid, cb("IR35 assessment added."));
  const update = useIR35Update(cid, cb("Updated."));
  const remove = useIR35Delete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  function openEdit(r?: any) {
    setModal({ open: true, rec: r ?? null });
    form.setFieldsValue(r ? { ...r, assessment_date: r.assessment_date ? dayjs(r.assessment_date) : null, contract_start: r.contract_start ? dayjs(r.contract_start) : null, contract_end: r.contract_end ? dayjs(r.contract_end) : null, reviewed_by_solicitor: !!r.reviewed_by_solicitor } : { determination: "undetermined" });
  }

  const columns = [
    { title: "Worker", dataIndex: "user_name", key: "u" },
    { title: "Assessment Date", dataIndex: "assessment_date", key: "d", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Determination", dataIndex: "determination", key: "det", render: (v: string) => <Tag color={DET_COLOR[v]}>{v?.toUpperCase()}</Tag> },
    { title: "Contract Start", dataIndex: "contract_start", key: "cs", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Contract End", dataIndex: "contract_end", key: "ce", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Solicitor Review", dataIndex: "reviewed_by_solicitor", key: "r", render: (v: number) => <Tag color={v ? "green" : "default"}>{v ? "Yes" : "No"}</Tag> },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => remove.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-between mb-3">
        <h3 className="font-semibold text-slate-700 text-sm">IR35 Assessments</h3>
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => openEdit()} style={{ background: "#4c1d95" }}>Add Assessment</Button>
      </div>
      <Table rowKey="id" dataSource={rows} columns={columns} loading={isLoading} size="small" pagination={{ current: page, pageSize: 20, total, onChange: setPage }} />
      <Modal open={modal.open} onCancel={() => setModal({ open: false, rec: null })} footer={null} destroyOnClose title={modal.rec ? "Edit IR35 Assessment" : "New IR35 Assessment"} width={520}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          const p = { ...vals, assessment_date: vals.assessment_date?.format("YYYY-MM-DD"), contract_start: vals.contract_start?.format("YYYY-MM-DD"), contract_end: vals.contract_end?.format("YYYY-MM-DD") };
          if (modal.rec) update.mutate({ id: modal.rec.id, ...p }); else create.mutate(p);
        }}>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="user_id" label="Worker" rules={[{ required: true }]}><Select showSearch options={(users as any[]).map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}`.trim() || u.email }))} /></Form.Item>
            <Form.Item name="assessment_date" label="Assessment Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="determination" label="Determination" rules={[{ required: true }]}><Select options={["inside","outside","undetermined"].map(v => ({ value: v, label: v.toUpperCase() }))} /></Form.Item>
            <Form.Item name="assessed_by" label="Assessed By (User ID)"><Input type="number" /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="contract_start" label="Contract Start"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="contract_end" label="Contract End"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <Form.Item name="evidence_notes" label="Evidence Notes"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="document_url" label="Document URL"><Input /></Form.Item>
          <Form.Item name="reviewed_by_solicitor" valuePropName="checked"><Checkbox>Reviewed by Solicitor</Checkbox></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={create.isPending || update.isPending}>Save</Button>
            <Button onClick={() => setModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
